import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { nip19 } from 'nostr-tools';
import { 
  Play, 
  Pause,
  Heart,
  Share2,
  User,
  X,
  Music,
  ListPlus,
  ExternalLink,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { usePlayer } from '@/contexts/PlayerContext';
import { useAuthor } from '@/hooks/useAuthor';
import { useLibrary } from '@/hooks/useLibrary';
import { useToast } from '@/hooks/useToast';
import { formatDuration } from '@/lib/audioUtils';
import { cn } from '@/lib/utils';
import type { MusicTrack } from '@/hooks/useMusicTracks';

interface TrackPreviewDialogProps {
  track: MusicTrack | null;
  open: boolean;
  onClose: () => void;
  allTracks?: MusicTrack[];
}

export function TrackPreviewDialog({ track, open, onClose, allTracks }: TrackPreviewDialogProps) {
  const { currentTrack, setCurrentTrack, setPlaylist, isPlaying, togglePlay } = usePlayer();
  const { isLiked, toggleLike } = useLibrary();
  const { toast } = useToast();
  
  const author = useAuthor(track?.event.pubkey);
  const metadata = author.data?.metadata;
  
  if (!track) return null;

  const liked = isLiked(track.id);
  const isCurrentTrack = currentTrack?.id === track.id;
  const npub = nip19.npubEncode(track.event.pubkey);

  const handlePlay = () => {
    if (allTracks) {
      setPlaylist(allTracks);
    }
    if (isCurrentTrack) {
      togglePlay();
    } else {
      setCurrentTrack(track);
    }
  };

  const handleShare = async () => {
    const url = `${window.location.origin}?track=${encodeURIComponent(track.id)}`;
    try {
      await navigator.clipboard.writeText(url);
      toast({ title: 'Link copied!' });
    } catch {
      toast({ title: 'Failed to copy link', variant: 'destructive' });
    }
  };

  const handleLike = () => {
    toggleLike(track.id);
    toast({ title: liked ? 'Removed from library' : 'Added to library' });
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm"
          />
          
          {/* Dialog */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[101] w-[90vw] max-w-md"
          >
            <div className="relative rounded-3xl overflow-hidden bg-gradient-to-b from-zinc-900 to-black border border-white/10 shadow-2xl">
              {/* Close button */}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>

              {/* Cover Art */}
              <div className="relative aspect-square">
                {track.coverImage ? (
                  <img 
                    src={track.coverImage} 
                    alt={track.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-orange-500/20 to-pink-500/20 flex items-center justify-center">
                    <Music className="h-24 w-24 text-white/20" />
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
                
                {/* Play button overlay */}
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handlePlay}
                  className="absolute bottom-6 right-6 h-16 w-16 rounded-full bg-orange-500 hover:bg-orange-400 flex items-center justify-center shadow-2xl shadow-orange-500/50 transition-colors"
                >
                  {isCurrentTrack && isPlaying ? (
                    <Pause className="h-7 w-7 text-white" fill="white" />
                  ) : (
                    <Play className="h-7 w-7 text-white ml-1" fill="white" />
                  )}
                </motion.button>
              </div>

              {/* Track Info */}
              <div className="p-6 space-y-4">
                <div>
                  <h2 className="text-xl font-bold truncate">{track.title}</h2>
                  <Link 
                    to={`/${npub}`}
                    onClick={onClose}
                    className="flex items-center gap-2 mt-2 group"
                  >
                    <Avatar className="h-6 w-6">
                      <AvatarImage src={metadata?.picture} />
                      <AvatarFallback><User className="h-3 w-3" /></AvatarFallback>
                    </Avatar>
                    <span className="text-sm text-muted-foreground group-hover:text-orange-400 transition-colors">
                      {track.artist}
                    </span>
                  </Link>
                </div>

                {/* Track details */}
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  {track.duration > 0 && (
                    <span>{formatDuration(track.duration)}</span>
                  )}
                  {track.album && (
                    <>
                      <span>•</span>
                      <span className="truncate">{track.album}</span>
                    </>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 pt-2">
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={handleLike}
                    className={cn(
                      'flex-1 rounded-full border-white/10',
                      liked && 'text-red-500 border-red-500/30'
                    )}
                  >
                    <Heart className={cn('h-5 w-5 mr-2', liked && 'fill-current')} />
                    {liked ? 'Saved' : 'Save'}
                  </Button>
                  
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={handleShare}
                    className="flex-1 rounded-full border-white/10"
                  >
                    <Share2 className="h-5 w-5 mr-2" />
                    Share
                  </Button>
                </div>

                {/* Go to artist */}
                <Link 
                  to={`/${npub}`}
                  onClick={onClose}
                  className="flex items-center justify-center gap-2 py-3 text-sm text-muted-foreground hover:text-orange-400 transition-colors"
                >
                  <ExternalLink className="h-4 w-4" />
                  View Artist Profile
                </Link>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
